import { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  MenuItem,
  Checkbox,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import { useFetchBonos, useCreateBono, useUpdateBono, useDeleteBono } from '@/shared/actions/erp/comisiones/comisiones.actions';
import { useFetchCitiesEnabled } from '@/shared/actions/erp';
import LoadingDialog from './LoadingDialog';
import type { BonoEscalon } from '@/lib/commissions/types';
import { BONOS_PREDEFINIDOS } from '@/lib/commissions/constants';

interface BonosFormProps {
  onSaved: () => void;
}

type BonoFormState = Omit<BonoEscalon, 'ciudad'> & { ciudad: string[] };

const buildBonoFormState = (bono?: BonoEscalon): BonoFormState => ({
  id: bono?.id,
  rol: bono?.rol ?? 'vendedor',
  umbral_desde: bono?.umbral_desde ?? 20,
  monto: bono?.monto ?? 0,
  ciudad:
    bono?.ciudad && bono.ciudad !== 'null'
      ? bono.ciudad.split(',').map((c) => c.trim().toUpperCase())
      : [],
});

export default function BonosForm({ onSaved }: BonosFormProps) {
  const { data: bonos, isLoading } = useFetchBonos();
  const createMutation = useCreateBono({ enableSuccessAlert: true });
  const updateMutation = useUpdateBono({ enableSuccessAlert: true });
  const deleteMutation = useDeleteBono();

  const { data: citiesResponse, isLoading: isLoadingCities } = useFetchCitiesEnabled({
    params: { page_size: 1000, estado: true }
  });

  const ciudades = citiesResponse?.data?.data || [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBono, setEditingBono] = useState<BonoFormState | null>(null);
  const [searchText, setSearchText] = useState('');
  const [loadingDialogOpen, setLoadingDialogOpen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const updateEditingBono = (updates: Partial<BonoFormState>) => {
    setEditingBono((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const handleCiudadChange = (event: SelectChangeEvent<string[]>) => {
    const rawValue = event.target.value;
    setEditingBono((prev) => {
      if (!prev) return prev;
      const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;

      if (value.includes('TODAS')) {
        return {
          ...prev,
          ciudad: prev.ciudad.includes('TODAS') ? [] : ['TODAS'],
        };
      }

      return {
        ...prev,
        ciudad: value.filter((v) => v !== 'TODAS'),
      };
    });
  };

  const handleOpenDialog = (bono?: BonoEscalon) => {
    setEditingBono(buildBonoFormState(bono));
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBono(null);
    setSearchText('');
  };

  const handleSave = async () => {
    if (!editingBono) return;

    try {
      const { ciudad, ...rest } = editingBono;
      const bonoData: Partial<BonoEscalon> = {
        ...rest,
        ciudad: ciudad.length === 0 || ciudad.includes('TODAS') ? null : ciudad.join(','),
      };

      if (editingBono.id) {
        await updateMutation.mutateAsync(bonoData as BonoEscalon);
      } else {
        await createMutation.mutateAsync(bonoData);
      }
      onSaved();
      handleCloseDialog();
    } catch (error: any) {
      // Error silencioso en producción
    }
  };

  const handleDelete = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar este bono?')) {
      await deleteMutation.mutateAsync(id);
      onSaved();
    }
  };

  const handleRestablecer = async () => {
    if (!confirm('¿Estás seguro de restablecer todos los bonos a los valores predefinidos? Esto eliminará todos los bonos existentes y creará nuevos con los valores predeterminados.')) {
      return;
    }

    try {
      setLoadingDialogOpen(true);
      setLoadingProgress(0);

      const totalSteps = (bonos?.length || 0) + BONOS_PREDEFINIDOS.length;
      let currentStep = 0;

      // Eliminar todos los bonos existentes
      if (bonos && bonos.length > 0) {
        for (const bono of bonos) {
          if (bono.id) {
            await deleteMutation.mutateAsync(bono.id);
            currentStep++;
            setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
          }
        }
      }

      // Crear los bonos predefinidos
      for (const bono of BONOS_PREDEFINIDOS) {
        await createMutation.mutateAsync(bono);
        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      }

      onSaved();
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    } catch (error) {
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    }
  };

  const filteredCiudades = useMemo(() => {
    const uniqueCiudades = ciudades.reduce((acc: any[], ciudad: any) => {
      const nombreNormalizado = ciudad.nombre.toLowerCase();
      if (!acc.some(c => c.nombre.toLowerCase() === nombreNormalizado)) {
        acc.push(ciudad);
      }
      return acc;
    }, []);

    if (!searchText) return uniqueCiudades;
    const searchLower = searchText.toLowerCase();
    return uniqueCiudades.filter((ciudad: any) =>
      ciudad.nombre.toLowerCase().includes(searchLower)
    );
  }, [ciudades, searchText]);

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Bonos por Escalón</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleRestablecer}
            size="small"
            color="warning"
            disabled={createMutation.isPending || deleteMutation.isPending}
          >
            Restablecer
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nuevo Bono
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rol</TableCell>
              <TableCell align="right">Umbral Desde</TableCell>
              <TableCell align="right">Monto</TableCell>
              <TableCell>Ciudad</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bonos && bonos.length > 0 ? (
              bonos.map((bono) => (
                <TableRow key={bono.id}>
                  <TableCell>{bono.rol}</TableCell>
                  <TableCell align="right">{bono.umbral_desde}</TableCell>
                  <TableCell align="right">${Number(bono.monto).toFixed(2)}</TableCell>
                  <TableCell>
                    {!bono.ciudad || bono.ciudad === 'null'
                      ? 'Todas'
                      : bono.ciudad === 'TODAS'
                        ? 'Todas'
                        : bono.ciudad.split(',').map(c => c.trim().charAt(0).toUpperCase() + c.trim().slice(1).toLowerCase()).join(', ')}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(bono)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => bono.id && handleDelete(bono.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay bonos registrados
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de edición */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBono?.id ? 'Editar Bono' : 'Nuevo Bono'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              label="Rol"
              value={editingBono?.rol || 'vendedor'}
              onChange={(e) => updateEditingBono({ rol: e.target.value })}
              fullWidth
              required
            >
              <MenuItem value="asesor">Asesor</MenuItem>
              <MenuItem value="vendedor">Vendedor</MenuItem>
              <MenuItem value="supervisor">Supervisor</MenuItem>
            </TextField>
            <TextField
              label="Umbral Desde"
              type="number"
              value={editingBono?.umbral_desde || 20}
              onChange={(e) => updateEditingBono({ umbral_desde: parseInt(e.target.value, 10) })}
              fullWidth
              required
              inputProps={{ min: 0 }}
              helperText="Número mínimo de ventas para obtener este bono"
            />
            <TextField
              label="Monto"
              type="number"
              value={editingBono?.monto || 0}
              onChange={(e) => updateEditingBono({ monto: parseFloat(e.target.value) })}
              fullWidth
              required
              inputProps={{ step: 0.01, min: 0 }}
              helperText="Monto del bono en dólares"
            />
            <FormControl fullWidth>
              <InputLabel id="ciudad-select-label">Ciudad</InputLabel>
              <Select
                labelId="ciudad-select-label"
                multiple
                value={editingBono?.ciudad ?? []}
                onChange={handleCiudadChange}
                onClose={() => setSearchText('')}
                input={<OutlinedInput label="Ciudad" />}
                renderValue={(selected: string[]) => {
                  if (selected.length === 0) return 'Ninguna seleccionada';
                  if (selected.includes('TODAS')) return 'Todas las ciudades';
                  return selected.join(', ');
                }}
                disabled={isLoadingCities}
                MenuProps={{
                  autoFocus: false,
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        py: 0.5,
                      }
                    }
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Buscar ciudad..."
                    fullWidth
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </Box>
                <MenuItem value="TODAS">
                  <Checkbox checked={(editingBono?.ciudad ?? []).includes('TODAS')} />
                  <ListItemText primary="Todas las ciudades" />
                </MenuItem>
                {filteredCiudades.map((ciudad: any) => {
                  const nombreNormalizado = ciudad.nombre.charAt(0).toUpperCase() + ciudad.nombre.slice(1).toLowerCase();
                  const ciudadMayusculas = ciudad.nombre.toUpperCase();
                  return (
                    <MenuItem
                      key={`bono-ciudad-${ciudad.id}`}
                      value={ciudadMayusculas}
                      disabled={(editingBono?.ciudad ?? []).includes('TODAS')}
                    >
                      <Checkbox checked={(editingBono?.ciudad ?? []).includes(ciudadMayusculas)} />
                      <ListItemText primary={nombreNormalizado} />
                    </MenuItem>
                  );
                })}
                {filteredCiudades.length === 0 && (
                  <MenuItem disabled>
                    <ListItemText primary="No se encontraron ciudades" />
                  </MenuItem>
                )}
              </Select>
              <Typography variant="caption" sx={{ mt: 0.5, ml: 1.75, color: 'text.secondary' }}>
                Selecciona "Todas" o una o varias ciudades específicas
              </Typography>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de carga */}
      <LoadingDialog
        open={loadingDialogOpen}
        progress={loadingProgress}
        title="Restableciendo bonos..."
        message="Eliminando bonos actuales y creando los valores predefinidos."
      />
    </Box>
  );
}

